import type { Attachment, AttachmentAdapter, CompleteAttachment, PendingAttachment } from "@assistant-ui/react";

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB

export interface GcsAttachmentAdapterOptions {
  baseUrl: string;
  getHeaders: () => Record<string, string>;
  getThreadId: () => string | null;
}

/**
 * Custom AttachmentAdapter that uploads files to GCS via the Superdoc
 * attachment API (signed URLs) and passes attachment IDs to the LLM.
 */
export class GcsAttachmentAdapter implements AttachmentAdapter {
  accept = "image/*,application/pdf,.doc,.docx,.txt,.csv,.xlsx,.xls,.xml,.json,.md";

  private baseUrl: string;
  private getHeaders: () => Record<string, string>;
  private getThreadId: () => string | null;

  constructor(options: GcsAttachmentAdapterOptions) {
    this.baseUrl = options.baseUrl;
    this.getHeaders = options.getHeaders;
    this.getThreadId = options.getThreadId;
  }

  async add({ file }: { file: File }): Promise<PendingAttachment> {
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(
        `File "${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is 20 MB.`,
      );
    }

    const type = file.type.startsWith("image/") ? "image" : "document";

    return {
      id: `pending_${Date.now()}_${file.name}`,
      type,
      name: file.name,
      contentType: file.type,
      file,
      status: { type: "requires-action", reason: "composer-send" },
    };
  }

  async send(attachment: PendingAttachment): Promise<CompleteAttachment> {
    const threadId = this.getThreadId();
    const file = attachment.file;

    // Step 1: Create attachment record and get signed upload URL
    const createRes = await fetch(`${this.baseUrl}/superdoc/attachments/create/`, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify({
        thread_id: threadId,
        filename: file.name,
        mime_type: file.type || "application/octet-stream",
        size: file.size,
      }),
    });

    if (!createRes.ok) {
      const err = await createRes.text();
      throw new Error(`Failed to create attachment: ${err}`);
    }

    const { attachment_id, upload_url } = (await createRes.json()) as {
      attachment_id: string;
      upload_url: string;
    };

    // Step 2: Upload file directly to GCS via signed PUT URL
    const uploadRes = await fetch(upload_url, {
      method: "PUT",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
      },
      body: file,
    });

    if (!uploadRes.ok) {
      throw new Error(`Failed to upload file to GCS: ${uploadRes.status}`);
    }

    // Return completed attachment with the backend attachment ID
    // The content array carries the ID so onNew() can extract it
    return {
      ...attachment,
      id: attachment_id,
      status: { type: "complete" },
      content: [
        {
          type: "text",
          text: `[Attached file: ${file.name}]`,
        },
      ],
    };
  }

  async remove(attachment: Attachment): Promise<void> {
    // Only delete from backend if it has a real (non-pending) ID
    if (attachment.id && !attachment.id.startsWith("pending_")) {
      try {
        await fetch(`${this.baseUrl}/superdoc/attachments/${attachment.id}/`, {
          method: "DELETE",
          headers: this.getHeaders(),
        });
      } catch {
        // Best-effort cleanup
      }
    }
  }
}
